import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Home, Loader2, ShieldCheck, Calendar, FileText, Info, ChevronDown, ChevronRight } from "lucide-react";
import { auth, projectsApi } from "../lib/api";
import { COMPANY } from "../data/mock";
import ProjectDetail from "../components/ProjectDetail";

const STATUS_STYLE = {
  pending: { color: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
  in_progress: { color: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  done: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
};

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };

const ClientPortal = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const user = auth.getUser();

  useEffect(() => {
    if (!user || user.role !== "client") { navigate("/portal/login"); return; }
    projectsApi.portal().then(setProjects).finally(() => setLoading(false));
  }, [user, navigate]);

  const logout = () => { auth.clear(); navigate("/portal/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="lis-container flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="LIS" className="h-14" />
            <div>
              <div className="text-xs text-slate-500">Client Portal</div>
              <div className="font-bold text-slate-900">LIINEX INFORMATION SYSTEMS</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-600 hover:text-brand hidden sm:inline-flex items-center gap-1"><Home size={14}/> Main Site</Link>
            <div className="text-right text-xs">
              <div className="font-semibold text-slate-900">{user?.name}</div>
              <div className="text-slate-500">{user?.email}</div>
            </div>
            <button onClick={logout} className="btn-outline text-sm"><LogOut size={14}/> Logout</button>
          </div>
        </div>
      </div>

      <div className="lis-container py-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-red-50 text-brand flex items-center justify-center"><ShieldCheck size={22}/></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Your Certification Projects</h1>
            <p className="text-sm text-slate-600">Track milestones, timelines and progress in real time.</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin"/> Loading...</div>
        ) : projects.length === 0 ? (
          <div className="mt-10 bg-white rounded-2xl border border-slate-100 p-10 text-center">
            <Info className="mx-auto text-brand" size={32}/>
            <h3 className="mt-3 font-semibold text-slate-900">No projects assigned yet</h3>
            <p className="mt-1 text-sm text-slate-500">Your LIS consultant will set up your certification project shortly. If you have questions, please reach us on WhatsApp or email department@liinexinfo.org.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {projects.map((p) => {
              const done = p.milestones.filter((m) => m.status === "done").length;
              const total = p.milestones.length;
              const pct = Math.round((done / total) * 100);
              return (
                <div key={p.id} className="bg-white rounded-2xl border border-slate-100 p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-bold text-slate-900">{p.standard}</h2>
                        <span className="chip">{p.client_company || p.client_name}</span>
                      </div>
                      <div className="mt-1 text-sm text-slate-600">{p.scope || "Full-scope implementation & certification"}</div>
                      {p.target_date && <div className="mt-1 text-xs text-slate-500 flex items-center gap-1"><Calendar size={12}/> Target certification: {fmt(p.target_date)}</div>}
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-extrabold text-brand">{pct}%</div>
                      <div className="text-xs text-slate-500">{done}/{total} milestones done</div>
                    </div>
                  </div>
                  <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-2 bg-brand transition-all" style={{ width: `${pct}%` }} />
                  </div>

                  <ol className="mt-6 relative border-l-2 border-slate-100 ml-3">
                    {p.milestones.map((m, i) => {
                      const s = STATUS_STYLE[m.status] || STATUS_STYLE.pending;
                      return (
                        <li key={m.key} className="mb-4 ml-4">
                          <span className={`absolute -left-[7px] w-3.5 h-3.5 rounded-full ${s.dot} border-2 border-white`} />
                          <div className={`inline-flex items-center gap-2 text-xs font-semibold px-2 py-0.5 rounded border ${s.color}`}>
                            {m.status.replace("_", " ")}
                          </div>
                          <div className="mt-1 font-semibold text-slate-900">{m.title}</div>
                          {m.note && <div className="text-xs text-slate-600 mt-0.5">{m.note}</div>}
                          {m.date && m.status !== "pending" && <div className="text-[11px] text-slate-400 mt-0.5">Updated {fmt(m.date)}</div>}
                        </li>
                      );
                    })}
                  </ol>

                  {p.notes && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-700 flex items-start gap-2">
                      <FileText size={14} className="text-brand mt-0.5"/> {p.notes}
                    </div>
                  )}

                  <button onClick={() => setExpanded(expanded === p.id ? null : p.id)} className="btn-outline text-sm mt-5">
                    {expanded === p.id ? <><ChevronDown size={14}/> Hide documents & discussion</> : <><ChevronRight size={14}/> View documents & discussion</>}
                  </button>

                  {expanded === p.id && <ProjectDetail projectId={p.id} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
